import React, { useEffect, useMemo } from 'react';

import type { IntlShape } from 'react-intl-next';
import { useIntl } from 'react-intl-next';

import { aiExperienceApplicationMessages } from '@atlaskit/editor-common/ai-messages';
import { ACTION } from '@atlaskit/editor-common/analytics';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { fg } from '@atlaskit/platform-feature-flags';
import { createUnifiedAnalyticsPayload } from '@atlassian/editor-ai-common/analytics/create-unified-analytics-payload';
import type { StreamFlowPacer } from '@atlassian/editor-ai-common/api/stream-flow-pacer';
import { DiscardScreen } from '@atlassian/generative-ai-modal/screens/Discard';

import type { AIPlugin } from '../../../../aiPluginType';
import { type StreamFns } from '../../../../pm-plugins/in-editor-direct-streaming/hooks/useInEditorDirectStreaming';
import { useAIExperienceCommonDataContext } from '../../../hooks/useAIExperienceCommonData';
import { convertExperienceName } from '../../../utils/analytics/analyticsFlow';
import { useAnalyticsFlowEventsQueue } from '../../../utils/analytics/analyticsFlowUtils';
import { useFireAIAnalyticsEvent } from '../../../utils/analytics/utils';
import type {
	AIExperienceMachineContext,
	HistoryEvent,
	LatestPrompt,
} from '../../../utils/xstate/get-ai-experience-service';

export const getDiscardMessage = ({
	formatMessage,
	experienceState,
}: {
	experienceState: string | Record<string, string>;
	formatMessage: IntlShape['formatMessage'];
}): string => {
	const discardPrompt = formatMessage(aiExperienceApplicationMessages.discardMessagePrompt);
	const discardResponse = formatMessage(aiExperienceApplicationMessages.discardMessageResponse);

	if (typeof experienceState === 'object' && 'user input state' in experienceState) {
		return discardPrompt;
	}

	if (
		experienceState === 'review state' ||
		(typeof experienceState === 'object' && 'loading state' in experienceState)
	) {
		return discardResponse;
	}

	return discardResponse;
};

export function DiscardScreenWithLogic({
	state,
	editCount,
	retryPromptCount,
	history,
	onceDiscard,
	channelId,
	streamFlowPacer,
}: {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	channelId?: string;
	discardStream?: StreamFns['discardStream'];
	editCount: number;
	history: {
		entries: HistoryEvent[];
		positionFromEnd: number;
		totalSize: number;
		traversals: number;
	};
	latestPrompt?: LatestPrompt;
	onceDiscard?: AIExperienceMachineContext['onceDiscard'];
	retryPromptCount: number;
	state: string | Record<string, string>;
	streamFlowPacer: StreamFlowPacer | null;
}): React.JSX.Element {
	const aiExperienceCommonData = useAIExperienceCommonDataContext();

	const {
		endExperience,
		sendToAIExperienceMachine,
		configItem,
		includeKnowledgeFromCurrentPage,
		appearance,
	} = aiExperienceCommonData;

	const inputOutputDiffRatio = useMemo(() => {
		const lastHistoryEvent = history.entries[history.positionFromEnd];
		return history.entries.length === 0 ? '' : lastHistoryEvent.inputOutputDiffRatio;
	}, [history]);

	const { formatMessage } = useIntl();
	const fireAIAnalyticsEvent = useFireAIAnalyticsEvent();
	const analyticsFlow = useAnalyticsFlowEventsQueue({
		stepName: 'discard',
		attributes: {
			editCount,
			retryPromptCount,
			inputOutputDiffRatio,
			includeKnowledgeFromCurrentPage,
		},
	});

	useEffect(() => {
		streamFlowPacer?.pause();
		return () => {
			streamFlowPacer?.resume();
		};
	}, [streamFlowPacer]);

	return (
		<DiscardScreen
			text={getDiscardMessage({
				formatMessage,
				experienceState: state,
			})}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			onCancel={function () {
				sendToAIExperienceMachine({ type: 'disable discard' });
			}}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			onDiscard={function (): void {
				/**
				 * Introduced in EDF-1443 to track discard event confirmations
				 * separately to normal discard events which get tracked
				 * earlier in this file
				 */
				fireAIAnalyticsEvent({
					payload: createUnifiedAnalyticsPayload({
						action: ACTION.DISMISSED,
						interactionID: channelId ?? analyticsFlow.getLastAiInteractionId(),
						experienceName: convertExperienceName(configItem.key.toString()),
						isProactive: false,
						attributes: analyticsFlow.getAiLifeCycleDynamicAttributes({ init: false }),
						invokedFrom: aiExperienceCommonData.lastTriggeredFrom,
						// Remove below flag when AIFC is fully rolled out in Jira, only required for old experience in Jira.
						isStreamingEnded: fg('platform_editor_ai_streaming_ended_analytics_attr')
							? !!analyticsFlow.getCommonAttributes().isStreamingEnded
							: undefined,
					}),
				});

				if (onceDiscard) {
					sendToAIExperienceMachine({ type: 'disable discard' });
					onceDiscard();
				} else {
					endExperience();
				}
			}}
			appearance={appearance}
		/>
	);
}
